import React, { useState, useEffect, useCallback } from 'react';
import useLanguage from '../../hooks/useLanguage';
import useTechSession from '../../hooks/useTechSession';
import TechButton from '../../components/tech/TechButton';
import { getTechnicians, getProjectsForDropdown } from '../../services/api';

/**
 * Technician login screen — redesigned flow:
 * Step 1: Select technician name (from all technicians)
 * Step 2: Select language (ONLY if not remembered from previous login)
 * Step 3: Select project (recent projects appear first, LIFO)
 *
 * Per-technician preferences saved to localStorage:
 * - Language (remembered for next login)
 * - Recent projects (LIFO order)
 */

const PREFS_KEY = 'airtouch_tech_prefs'; // { [techId]: { language, recentProjects: [{id,name,date}] } }

function loadPrefs() {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY)) || {};
  } catch { return {}; }
}

function savePrefs(prefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

function getTechPrefs(techId) {
  const prefs = loadPrefs();
  return prefs[techId] || { language: null, recentProjects: [] };
}

function saveTechLanguage(techId, lang) {
  const prefs = loadPrefs();
  if (!prefs[techId]) prefs[techId] = { language: null, recentProjects: [] };
  prefs[techId].language = lang;
  savePrefs(prefs);
}

function saveTechRecentProject(techId, projectId, projectName) {
  const prefs = loadPrefs();
  if (!prefs[techId]) prefs[techId] = { language: null, recentProjects: [] };
  // Remove if exists, then push to front (LIFO)
  prefs[techId].recentProjects = prefs[techId].recentProjects.filter(p => p.id !== projectId);
  prefs[techId].recentProjects.unshift({ id: projectId, name: projectName, date: new Date().toISOString() });
  // Keep last 20
  prefs[techId].recentProjects = prefs[techId].recentProjects.slice(0, 20);
  savePrefs(prefs);
}


export default function TechLogin({ onLogin }) {
  const { language, setLanguage, t, languages, isRTL } = useLanguage();
  const { login } = useTechSession();

  // Steps: 'name' -> 'language' (optional) -> 'project'
  const [step, setStep] = useState('name');
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedTech, setSelectedTech] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load all technicians on mount
  useEffect(() => {
    setLoading(true);
    getTechnicians()
      .then(data => setAllTechnicians(data.technicians || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Load projects when entering project step
  useEffect(() => {
    if (step === 'project') {
      setLoading(true);
      getProjectsForDropdown()
        .then(data => {
          let allProjects = data.projects || [];
          // Sort: recent projects first (LIFO), then alphabetical
          if (selectedTech) {
            const prefs = getTechPrefs(selectedTech.id);
            const recentIds = prefs.recentProjects.map(p => p.id);
            const recent = [];
            const rest = [];
            for (const p of allProjects) {
              const recentIndex = recentIds.indexOf(p.id);
              if (recentIndex !== -1) {
                recent.push({ ...p, _recentIndex: recentIndex, _isRecent: true });
              } else {
                rest.push(p);
              }
            }
            // Sort recent by LIFO (index 0 = most recent)
            recent.sort((a, b) => a._recentIndex - b._recentIndex);
            rest.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            allProjects = [...recent, ...rest];
          }
          setProjects(allProjects);
        })
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, [step, selectedTech]);

  const handleSelectTechnician = useCallback((tech) => {
    setSelectedTech(tech);
    const prefs = getTechPrefs(tech.id);

    if (prefs.language) {
      // Language remembered — skip to project selection
      setLanguage(prefs.language);
      setStep('project');
    } else {
      // First time — ask language
      setStep('language');
    }
  }, [setLanguage]);

  const handleSelectLanguage = useCallback((langCode) => {
    setLanguage(langCode);
    if (selectedTech) {
      saveTechLanguage(selectedTech.id, langCode);
    }
    setStep('project');
  }, [selectedTech, setLanguage]);

  const handleSelectProject = useCallback((proj) => {
    setSelectedProject(proj);
    if (selectedTech) {
      // Save this project as recently used
      saveTechRecentProject(selectedTech.id, proj.id, proj.name);
      // Complete login
      login(selectedTech.id, selectedTech.fullName, proj.id, proj.name, language);
      if (onLogin) onLogin();
    }
  }, [selectedTech, language, login, onLogin]);

  // Filter technicians by search
  const filteredTechs = searchQuery.trim()
    ? allTechnicians.filter(tech =>
        (tech.fullName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (tech.phoneNumber || '').includes(searchQuery)
      )
    : allTechnicians;

  // Filter projects by search
  const [projectSearch, setProjectSearch] = useState('');
  const filteredProjects = projectSearch.trim()
    ? projects.filter(p =>
        (p.name || '').toLowerCase().includes(projectSearch.toLowerCase())
      )
    : projects;

  return (
    <div style={{ ...styles.container, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={styles.logo}>
        <div style={styles.logoText}>AirTouch</div>
        <div style={styles.logoSub}>Technician</div>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
          <span onClick={() => setError(null)} style={styles.dismiss}>x</span>
        </div>
      )}

      {/* Step 1: Select Technician Name */}
      {step === 'name' && (
        <div style={styles.card}>
          <h2 style={styles.title}>{t.selectName}</h2>

          {/* Search box */}
          <input
            type="text"
            placeholder={isRTL ? 'חפש שם...' : 'Search name...'}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={styles.searchInput}
            autoFocus
          />

          {loading ? (
            <div style={styles.loading}>{t.loading}</div>
          ) : filteredTechs.length === 0 ? (
            <div style={styles.empty}>
              {searchQuery
                ? (isRTL ? 'לא נמצאו תוצאות' : 'No results found')
                : (isRTL ? 'אין טכנאים רשומים' : 'No technicians registered')
              }
            </div>
          ) : (
            <div style={styles.optionList}>
              {filteredTechs.map(tech => (
                <button
                  key={tech.id}
                  onClick={() => handleSelectTechnician(tech)}
                  style={styles.optionBtn}
                >
                  <span style={styles.avatar}>
                    {tech.fullName?.[0]?.toUpperCase() || '?'}
                  </span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{tech.fullName}</div>
                    <div style={{ fontSize: 11, color: '#8b949e' }}>
                      {tech.role} {tech.phoneNumber ? `\u2022 ${tech.phoneNumber}` : ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Language (only if not remembered) */}
      {step === 'language' && (
        <div style={styles.card}>
          <h2 style={styles.title}>{t.selectLanguage}</h2>
          <div style={{ fontSize: 13, color: '#8b949e', textAlign: 'center', marginBottom: 12 }}>
            {selectedTech?.fullName}
          </div>
          <div style={styles.optionList}>
            {Object.entries(languages).map(([code, info]) => (
              <button
                key={code}
                onClick={() => handleSelectLanguage(code)}
                style={{
                  ...styles.optionBtn,
                  ...(language === code ? styles.optionBtnActive : {}),
                }}
              >
                <span style={{ fontSize: 28 }}>{info.flag}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{info.nativeName}</div>
                  <div style={{ fontSize: 12, color: '#8b949e' }}>{info.name}</div>
                </div>
              </button>
            ))}
          </div>
          <TechButton variant="ghost" onClick={() => { setStep('name'); setSelectedTech(null); }} style={{ marginTop: 12 }}>
            {'\u2190'} {isRTL ? 'חזרה' : 'Back'}
          </TechButton>
        </div>
      )}

      {/* Step 3: Project (recent first) */}
      {step === 'project' && (
        <div style={styles.card}>
          <h2 style={styles.title}>{t.selectProject}</h2>
          <div style={{ fontSize: 13, color: '#8b949e', textAlign: 'center', marginBottom: 12 }}>
            {selectedTech?.fullName}
          </div>

          {/* Search box */}
          <input
            type="text"
            placeholder={isRTL ? 'חפש פרויקט...' : 'Search project...'}
            value={projectSearch}
            onChange={e => setProjectSearch(e.target.value)}
            style={styles.searchInput}
          />

          {loading ? (
            <div style={styles.loading}>{t.loading}</div>
          ) : (
            <div style={styles.optionList}>
              {filteredProjects.map((p, idx) => (
                <button
                  key={p.id}
                  onClick={() => handleSelectProject(p)}
                  style={{
                    ...styles.optionBtn,
                    ...(p._isRecent ? { borderColor: '#6c5ce744', background: '#6c5ce710' } : {}),
                  }}
                >
                  <span style={{ fontSize: 14, color: '#8b949e' }}>
                    {p.country === 'IN' ? '\uD83C\uDDEE\uD83C\uDDF3' : p.country === 'IL' ? '\uD83C\uDDEE\uD83C\uDDF1' : '\uD83C\uDF10'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: '#8b949e' }}>
                      {p.country}
                      {p._isRecent && (
                        <span style={{ color: '#6c5ce7', marginInlineStart: 8 }}>
                          {isRTL ? '★ אחרון' : '★ Recent'}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <TechButton variant="ghost" onClick={() => { setStep('name'); setSelectedTech(null); setProjectSearch(''); }} style={{ marginTop: 12 }}>
            {'\u2190'} {isRTL ? 'חזרה' : 'Back'}
          </TechButton>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#0d1117',
    color: '#e6edf3',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 16px',
    fontFamily: "'Inter', -apple-system, sans-serif",
    maxWidth: 480,
    margin: '0 auto',
  },
  logo: {
    textAlign: 'center',
    marginBottom: 32,
  },
  logoText: {
    fontSize: 28,
    fontWeight: 700,
    color: '#6c5ce7',
  },
  logoSub: {
    fontSize: 14,
    color: '#8b949e',
    marginTop: 4,
  },
  card: {
    width: '100%',
    maxWidth: 360,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 16,
    textAlign: 'center',
  },
  searchInput: {
    width: '100%',
    padding: '10px 14px',
    fontSize: 14,
    background: '#161b22',
    border: '1px solid #30363d',
    borderRadius: 10,
    color: '#e6edf3',
    marginBottom: 12,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
  },
  optionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 380,
    overflowY: 'auto',
  },
  optionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    background: '#161b22',
    border: '1px solid #30363d',
    borderRadius: 12,
    color: '#e6edf3',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    fontFamily: 'inherit',
  },
  optionBtnActive: {
    borderColor: '#6c5ce7',
    background: '#6c5ce720',
  },
  avatar: {
    width: 36, height: 36, borderRadius: '50%',
    background: '#6c5ce7', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16, fontWeight: 700, flexShrink: 0,
  },
  loading: {
    textAlign: 'center',
    padding: 32,
    color: '#8b949e',
  },
  empty: {
    color: '#8b949e',
    textAlign: 'center',
    padding: 24,
  },
  error: {
    background: '#d6303122',
    color: '#d63031',
    padding: '10px 16px',
    borderRadius: 8,
    marginBottom: 16,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    maxWidth: 360,
  },
  dismiss: {
    cursor: 'pointer',
    padding: '0 4px',
    fontWeight: 700,
  },
};
